import { Badge, DrawerContent, DrawerHeader, DrawerRoot, DrawerTitle, Skeleton } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStrategistConceptCard } from '@/entities/strategist';
import type { StrategistConceptCardRequest } from '@/entities/strategist';
import { formatLiftPp, formatTargetRate, liftColorClass } from '@/shared/lib/utils';
import { useConceptCardStore } from '../model/store';
import { ConceptExample } from './ConceptExample';
import { MetaRow } from './MetaRow';

/** Сколько примеров реплик запрашивать для карточки понятия. */
const EXAMPLES_LIMIT = 15;

/**
 * Карточка понятия раздела «Стратег», общая для всех его страниц: показатели понятия в
 * текущем отборе и примеры его реплик. Открывается нажатием на понятие — само понятие и
 * отбор, в котором его смотрят, приходят через {@link useConceptCardStore}.
 *
 * Монтируется по одному разу на каждой странице раздела.
 */
export const ConceptCardDrawer: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);
  const conceptId = useConceptCardStore((store) => store.conceptId);
  const scope = useConceptCardStore((store) => store.scope);
  const close = useConceptCardStore((store) => store.close);

  const body = useMemo<StrategistConceptCardRequest>(
    () => ({
      conceptId: conceptId ?? '',
      filters: scope?.filters ?? [],
      dateFrom: scope?.dateFrom,
      dateTo: scope?.dateTo,
      limit: EXAMPLES_LIMIT,
    }),
    [conceptId, scope],
  );

  const { data, isLoading } = useStrategistConceptCard(body);

  const handleOpenChange = (open: boolean) => {
    if (!open) close();
  };

  return (
    <DrawerRoot open={conceptId !== null} onOpenChange={handleOpenChange}>
      <DrawerContent className="m-4 ml-0 rounded-2xl">
        <DrawerHeader>
          <DrawerTitle>{data?.label ?? ''}</DrawerTitle>
        </DrawerHeader>

        <div className="flex flex-col gap-4 overflow-auto p-6 pt-0">
          {isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-6 w-40" />
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-20 w-full" />
              ))}
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                {data?.role && (
                  <MetaRow label={t('strategist:conceptCard.role')}>
                    <Badge variant="secondary" outline>
                      {data.role}
                    </Badge>
                  </MetaRow>
                )}
                {data?.nodeType && <MetaRow label={t('strategist:conceptCard.nodeType')}>{data.nodeType}</MetaRow>}
                <MetaRow label={t('strategist:conceptCard.size')}>{data?.size ?? 0}</MetaRow>
                <MetaRow label={t('strategist:conceptCard.nDialogs')}>{data?.nDialogs ?? 0}</MetaRow>
                <MetaRow label={t('strategist:conceptCard.targetRate')}>{formatTargetRate(data?.targetRate)}</MetaRow>
                <MetaRow label={t('strategist:conceptCard.lift')}>
                  <span className={liftColorClass(data?.lift ?? null)}>
                    {formatLiftPp(data?.lift ?? null)} {t('strategist:conceptCard.pp')}
                  </span>
                </MetaRow>
                <MetaRow label={t('strategist:conceptCard.baseline')}>{formatTargetRate(data?.baselineRate)}</MetaRow>
              </div>

              {data && data.examples.length > 0 ? (
                <div className="flex flex-col gap-3">
                  <span className="text-sm font-medium text-foreground">{t('strategist:conceptCard.examples')}</span>
                  {data.examples.map((example, index) => (
                    <ConceptExample key={`${example.cid}-${index}`} example={example} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('common:emptyTable')}</p>
              )}
            </>
          )}
        </div>
      </DrawerContent>
    </DrawerRoot>
  );
};
